import { useEffect, useMemo, useState } from "react";

import { getPracticesByNumbers } from "../../api";
import { userPractices } from "./access";

/**
 * The user's practices as selectable options, labelled by name.
 *
 * The practice NUMBER stays the value — every WhatsApp route is keyed by it —
 * but staff think in names, so that is what the dropdown shows.
 *
 * Shared by the settings panel and the dashboard inbox so the two selectors
 * cannot drift in how they label or order practices.
 */
export function usePracticeOptions() {
  const practiceNumbers = useMemo(() => userPractices(), []);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(practiceNumbers.length > 0);

  useEffect(() => {
    if (!practiceNumbers.length) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const practices = await getPracticesByNumbers(practiceNumbers);
        if (cancelled) return;
        setNames(
          Object.fromEntries(
            (practices || []).map((p) => [
              String(p.practiceNumber),
              p.practiceName,
            ]),
          ),
        );
      } catch (_err) {
        // Fall back to numbers rather than an empty dropdown — the selector
        // still works, it just reads less well.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [practiceNumbers]);

  const options = useMemo(
    () =>
      practiceNumbers
        .map((practiceNumber) => ({
          practiceNumber,
          // A practice whose name has not resolved shows its number, so the
          // option is never blank.
          label: names[practiceNumber] || practiceNumber,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "en-GB")),
    [practiceNumbers, names],
  );

  const labelFor = (practiceNumber) =>
    names[String(practiceNumber)] || String(practiceNumber || "");

  return { options, practiceNumbers, labelFor, loading };
}

export default usePracticeOptions;
